import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactGridLayout, { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { useSearchParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  sensorDevicesSelectors,
  useGetHomeQuery,
} from '../../app/services/api';
import { DotButtonPagination } from '../../components/DotButtonPagination';
import { RoomCard } from '../../components/RoomCard';
import { mediaBreakpoints } from '../../configs/mediaBreakpoints';
import { useIsAdminOrManagerMode } from '../../hooks/useIsAdminOrManagerMode';
import { mergeDisplaySettings } from '../../types/DisplaySettings';
import { SensorDevice } from '../../types/SensorDevice';
import { companySelectors, companySlice } from '../Company/companySlice';
import { roomCardsSlice } from './roomCardsSlice';

const cols = { xl: 5, lg: 5, md: 3, sm: 1, xs: 1 };
const ResponsiveGridLayout = WidthProvider(Responsive);
// Reference row height used to decide how many rows (and therefore how many
// cards) fit on a page. It is intentionally font-independent so the visible
// card count stays constant; the actual card height fills the screen.
const BASE_CARD_HEIGHT = 180;
const layoutStorageKey = '@@knock-layout@@';
// Lower bound for the configurable auto-roll interval, so it can never be set
// fast enough to be unreadable.
const MIN_PAGE_ROLL_INTERVAL_MS = 3000;
const margin: [number, number] = [20, 20];
// Wall displays stay open for days. Periodically refetch the full board so
// roster changes and admin-edited display settings (font sizes, colors)
// propagate to every screen automatically, without a manual reload. Live
// status flips already arrive over the SSE stream; this only covers data the
// stream doesn't push.
const HOME_POLLING_INTERVAL_MS = 60_000;
export function RoomCardsGrid() {
  // const isSmall = useIsSmall();
  // const isMobile = useIsMobile();
  const ref = useRef<null | HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const { data } = useGetHomeQuery(undefined, {
    pollingInterval: HOME_POLLING_INTERVAL_MS,
  });
  const isAdminOrManagerMode = useIsAdminOrManagerMode();
  const { filterSensorStatus, viewport } = useAppSelector(
    companySelectors.selectState,
  );
  const displaySettings = useMemo(
    () => mergeDisplaySettings(data?.displaySettings),
    [data?.displaySettings],
  );
  const [filteredSensorDevices, setFilteredSensorDevices] = useState<
    SensorDevice[]
  >([]);
  const [breakPoint, setBreakPoint] = useState<string | undefined>(undefined);
  const [itemsPerPage, setItemsPerPage] = useState(0);
  const [pageSize, setPageSize] = useState(0);
  // Phones (and small tablets) use a single-column scrolling list instead of
  // the paged wall-display grid: no pagination, no auto-roll, compact cards.
  const isMobile =
    breakPoint !== undefined
      ? breakPoint === 'xs' || breakPoint === 'sm'
      : !!viewport && viewport[0] < mediaBreakpoints.md;
  const [searchParams, setSearchParams] = useSearchParams();
  // The current page is driven by the `page` query param (1-indexed) so a
  // page can be linked / refreshed / shared and land directly on it.
  const pageParam = parseInt(searchParams.get('page') ?? '', 10);
  const currentPage = useMemo(() => {
    const zeroBased =
      (Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1) - 1;
    if (pageSize <= 0) return 0;
    return Math.min(Math.max(zeroBased, 0), pageSize - 1);
  }, [pageParam, pageSize]);
  const goToPage = useCallback(
    (index: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('page', String(index + 1));
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );
  // The number of rows is decided by a fixed reference height, so the visible
  // card count does NOT change when the font sizes change. The card height then
  // expands to fill the available space — the largest cards that still keep
  // that count (e.g. 5x4 = 20 on a 24"/1080p board, regardless of font size).
  const { maxRows, cardHeight } = useMemo(() => {
    if (isMobile) {
      // Single-column scroll list: size each card to its content so larger
      // title fonts never get clipped. The tallest card has a two-line title
      // (name + position).
      const title = displaySettings.cardTitleFontSize;
      const position = displaySettings.cardPositionFontSize;
      const nameLine = title * 1.18;
      const positionLine = position * 1.18 + 7;
      const chrome = 32 /* content padding */ + 20; /* title margins */
      const contentH = Math.ceil(nameLine + positionLine + chrome);
      return { maxRows: 1, cardHeight: Math.max(120, contentH + 12) };
    }
    if (viewport && ref.current) {
      const height = viewport[1];
      const y = ref.current.getBoundingClientRect().top + window.scrollY;
      const available = height - y - 60;
      const rows = Math.max(
        1,
        Math.floor(available / (BASE_CARD_HEIGHT + margin[1])),
      );
      // fill the available height: container padding (margin on top+bottom) +
      // inter-row gaps are subtracted, the rest is split evenly across rows.
      const filled = Math.floor(
        (available - 2 * margin[1] - (rows - 1) * margin[1]) / rows,
      );
      return { maxRows: rows, cardHeight: Math.max(120, filled) };
    }
    return { maxRows: 0, cardHeight: BASE_CARD_HEIGHT };
  }, [viewport, isMobile, displaySettings]);

  const [layouts, cards] = useMemo(() => {
    const layouts: ReactGridLayout.Layouts = {
      xl: [],
      lg: [],
      md: [],
      sm: [],
      xs: [],
    };

    const cards: JSX.Element[] = [];
    const handleEditClick = (cardInfo: SensorDevice) => () => {
      dispatch(roomCardsSlice.actions.setIsEditModalOpen(true));
      dispatch(roomCardsSlice.actions.setSelectedCardId(cardInfo.id));
    };

    filteredSensorDevices
      .slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage)
      .forEach((sensorDevice, i) => {
        layouts.xl.push({
          h: 1,
          w: 1,
          i: sensorDevice.id,
          x: i % cols.xl,
          y: Math.floor(i / cols.xl),
        });
        layouts.lg.push({
          h: 1,
          w: 1,
          i: sensorDevice.id,
          x: i % cols.lg,
          y: Math.floor(i / cols.lg),
        });
        layouts.md.push({
          h: 1,
          w: 1,
          i: sensorDevice.id,
          x: i % cols.md,
          y: Math.floor(i / cols.md),
        });
        layouts.sm.push({
          h: 1,
          w: 1,
          i: sensorDevice.id,
          x: i % cols.sm,
          y: Math.floor(i / cols.sm),
        });
        layouts.xs.push({
          h: 1,
          w: 1,
          i: sensorDevice.id,
          x: i % cols.xs,
          y: Math.floor(i / cols.xs),
        });
        cards.push(
          <div key={sensorDevice.id}>
            <RoomCard
              isAdminOrManagerMode={isAdminOrManagerMode}
              value={sensorDevice}
              onEditClick={handleEditClick(sensorDevice)}
              displaySettings={displaySettings}
            />
          </div>,
        );
      });
    return [layouts, cards];
  }, [
    dispatch,
    isAdminOrManagerMode,
    currentPage,
    itemsPerPage,
    filteredSensorDevices,
    displaySettings,
  ]);

  const handleLayoutChange = useCallback(
    (currentLayout: ReactGridLayout.Layout[]) => {
      if (global.localStorage && data && filterSensorStatus === 'all') {
        global.localStorage.setItem(
          layoutStorageKey,
          JSON.stringify({
            [data.companyName]: currentLayout,
          }),
        );
      }
    },
    [data, filterSensorStatus],
  );

  const handleBreakpointChange = useCallback((newBreakpoint: string) => {
    setBreakPoint(newBreakpoint);
  }, []);

  useEffect(() => {
    if (isMobile) {
      // show every device in one scrollable column (no paging)
      setItemsPerPage(filteredSensorDevices.length);
    } else if (maxRows) {
      const itemsPerPage =
        maxRows * cols[(breakPoint || 'xl') as keyof typeof cols];
      setItemsPerPage(itemsPerPage);
    } else {
      setItemsPerPage(0);
    }
  }, [isMobile, breakPoint, maxRows, filteredSensorDevices]);

  useEffect(() => {
    if (data) {
      let sensorDevices = sensorDevicesSelectors.selectAll(data);
      if (filterSensorStatus !== 'all') {
        sensorDevices = sensorDevices.filter((sensorDevice) => {
          return sensorDevice.status === filterSensorStatus;
        });
      }
      setFilteredSensorDevices(sensorDevices);
    } else {
      setFilteredSensorDevices([]);
    }
  }, [data, filterSensorStatus]);

  useEffect(() => {
    if (itemsPerPage > 0) {
      const pageSize = Math.ceil(filteredSensorDevices.length / itemsPerPage);
      setPageSize(pageSize);
    } else {
      setPageSize(0);
    }
  }, [filteredSensorDevices, itemsPerPage]);

  // Auto-roll through pages when the board has more than one page. We keep the
  // latest paging state in a ref and start a single interval (only when the
  // multi-page state toggles) so re-renders never reset the timer before it
  // fires. The page is written back to the `page` query param with `replace`.
  const rollRef = useRef({ pageSize, currentPage, setSearchParams });
  rollRef.current = { pageSize, currentPage, setSearchParams };
  const isMultiPage = pageSize > 1;
  const rollIntervalMs = Math.max(
    MIN_PAGE_ROLL_INTERVAL_MS,
    displaySettings.pageRollIntervalSec * 1000,
  );
  useEffect(() => {
    if (!isMultiPage) {
      return;
    }
    const timer = setInterval(() => {
      const {
        pageSize: ps,
        currentPage: cp,
        setSearchParams: setParams,
      } = rollRef.current;
      if (ps <= 1) {
        return;
      }
      const nextPage = ((cp + 1) % ps) + 1; // 1-indexed, wraps to 1
      setParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set('page', String(nextPage));
          return params;
        },
        { replace: true },
      );
    }, rollIntervalMs);
    return () => clearInterval(timer);
  }, [isMultiPage, rollIntervalMs]);

  // Reset to the first page when the status filter changes (but not on the
  // initial mount, so a `?page=N` deep link is honored on load).
  const prevFilterRef = useRef(filterSensorStatus);
  useEffect(() => {
    if (prevFilterRef.current !== filterSensorStatus) {
      prevFilterRef.current = filterSensorStatus;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('page', '1');
          return next;
        },
        { replace: true },
      );
    }
  }, [filterSensorStatus, setSearchParams]);

  useEffect(() => {
    dispatch(companySlice.actions.setIsClippedMode(!isMobile));
  }, [dispatch, isMobile]);

  return (
    <div ref={ref}>
      <ResponsiveGridLayout
        breakpoints={mediaBreakpoints}
        layouts={layouts}
        cols={cols}
        rowHeight={cardHeight}
        isResizable={false}
        isDraggable={false} // WidthProvider option
        isBounded={true}
        margin={margin}
        measureBeforeMount={false}
        compactType={'horizontal'} //horizontal로 설정해야 드래그 시 아래로 이동하는 것을 방지
        onLayoutChange={handleLayoutChange}
        onBreakpointChange={handleBreakpointChange}
      >
        {cards}
      </ResponsiveGridLayout>
      {pageSize > 1 ? (
        <div
          style={{
            position: 'absolute',
            bottom: breakPoint === 'xs' ? 10 : 30,
            left: '50%',
            transform: 'translateX(-50%)',
            margin: '0 auto',
            textAlign: 'center',
          }}
        >
          <DotButtonPagination
            pageSize={pageSize}
            currentPage={currentPage}
            onClick={goToPage}
          />
        </div>
      ) : null}
    </div>
  );
}
